import type {
  ExecutiveMemoFailedStage,
  NewsJobMode,
  NewsNotificationType,
} from "./api";
import { getKnowledgeItemDisplayName } from "./knowledge";

export type ReceiptJobNotification = {
  kind: "receipt";
  jobId: string;
  status: "processing" | "completed" | "failed" | "needs_input";
  processedAt?: string;
  extractedSummary?: string;
  failedStage?: string;
  failedError?: string;
};

export type ReceiptNeedsReviewNotification = {
  kind: "receipt_needs_review";
  jobId: string;
};

export type ExecutiveMemoJobNotification = {
  kind: "executive_memo";
  jobId: string;
  status: "processing" | "completed" | "failed" | "timed_out" | "needs_input";
  processedAt?: string;
  memoId?: string;
  title?: string;
  summary?: string;
  failedStage?: ExecutiveMemoFailedStage;
  failedError?: string;
};

export type NewsJobNotification = {
  kind: "news";
  jobId: string;
  status: "processing" | "completed" | "failed" | "cancelled" | "needs_input";
  processedAt?: string;
  keyword?: string;
  notificationType?: NewsNotificationType;
  mode?: NewsJobMode;
  reportRunId?: string;
  failedStage?: string;
  failedError?: string;
  callbackMessage?: string;
  summary?: string;
};

export type KnowledgeJobNotification = {
  kind: "knowledge";
  userId: string;
  knowledgeItemId: string;
  status: "processing" | "completed" | "failed";
  processedAt?: string;
  title?: string;
  type?: "pdf" | "url" | "executive_memo" | "company_research";
  failedError?: string;
};

export type LevelUpNotification = {
  kind: "level_up";
  characterId: string;
  rankName: string;
};

export type PendingJobNotification =
  | ReceiptJobNotification
  | ReceiptNeedsReviewNotification
  | ExecutiveMemoJobNotification
  | NewsJobNotification
  | KnowledgeJobNotification
  | LevelUpNotification;
export type PendingJobNotificationKind = PendingJobNotification["kind"];
type PendingJobStatus =
  | ReceiptJobNotification["status"]
  | ExecutiveMemoJobNotification["status"]
  | NewsJobNotification["status"]
  | KnowledgeJobNotification["status"];

export const JOB_NOTIFICATION_STORAGE_KEY = "wizmate.job-notifications.v1";
export const JOB_NOTIFICATION_EVENT = "wizmate:job-notifications";

function normalize(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
}

function hasWindow() {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

function isNotificationStatus(
  value: unknown
): value is PendingJobStatus {
  return value === "completed" || value === "failed" || value === "cancelled" || value === "timed_out" || value === "needs_input" || value === "processing";
}

function isCompletedOrFailedStatus(value: unknown): value is "completed" | "failed" | "cancelled" {
  return value === "completed" || value === "failed" || value === "cancelled";
}

function isResolvedOrProcessingStatus(
  value: unknown
): value is "completed" | "failed" | "cancelled" | "processing" {
  return isCompletedOrFailedStatus(value) || value === "processing";
}

function isNotificationKind(
  value: unknown
): value is PendingJobNotificationKind {
  return (
    value === "receipt" ||
    value === "receipt_needs_review" ||
    value === "executive_memo" ||
    value === "news" ||
    value === "knowledge" ||
    value === "level_up"
  );
}

function parsePendingJobNotification(value: unknown): PendingJobNotification | null {
  if (!value || typeof value !== "object") return null;

  const candidate = value as Partial<PendingJobNotification>;
  const kind = candidate.kind;

  if (!isNotificationKind(kind)) {
    return null;
  }

  if (kind === "receipt") {
    const jobId = normalize((candidate as Partial<ReceiptJobNotification>).jobId);
    const status = candidate.status;
    if (!jobId || !isResolvedOrProcessingStatus(status)) {
      return null;
    }
    return {
      kind,
      jobId,
      status,
      processedAt: normalize(candidate.processedAt),
      extractedSummary: normalize((candidate as Partial<ReceiptJobNotification>).extractedSummary),
      failedStage: normalize(candidate.failedStage),
      failedError: normalize(candidate.failedError),
    };
  }

  if (kind === "receipt_needs_review") {
    const jobId = normalize((candidate as Partial<ReceiptNeedsReviewNotification>).jobId);
    if (!jobId) {
      return null;
    }
    return {
      kind,
      jobId,
    };
  }

  if (kind === "executive_memo") {
    const executiveMemoCandidate = candidate as Partial<ExecutiveMemoJobNotification>;
    const jobId = normalize(executiveMemoCandidate.jobId);
    const status = executiveMemoCandidate.status;
    if (!jobId || !isNotificationStatus(status)) {
      return null;
    }
    return {
      kind,
      jobId,
      status,
      processedAt: normalize(candidate.processedAt),
      memoId: normalize(executiveMemoCandidate.memoId),
      title: normalize(executiveMemoCandidate.title),
      summary: normalize(executiveMemoCandidate.summary),
      failedStage: normalize(candidate.failedStage) as ExecutiveMemoFailedStage | undefined,
      failedError: normalize(candidate.failedError),
    };
  }

  if (kind === "news") {
    const newsCandidate = candidate as Partial<NewsJobNotification>;
    const jobId = normalize(newsCandidate.jobId);
    const status = candidate.status;
    if (!jobId || !isResolvedOrProcessingStatus(status)) {
      return null;
    }
    const notificationType =
      newsCandidate.notificationType === "line" ||
      newsCandidate.notificationType === "google_chat" ||
      newsCandidate.notificationType === "email"
        ? newsCandidate.notificationType
        : undefined;
    const mode = newsCandidate.mode === "news" || newsCandidate.mode === "report"
      ? newsCandidate.mode
      : undefined;

    return {
      kind,
      jobId,
      status,
      processedAt: normalize(candidate.processedAt),
      keyword: normalize(newsCandidate.keyword),
      notificationType,
      mode,
      reportRunId: normalize(newsCandidate.reportRunId),
      failedStage: normalize(candidate.failedStage),
      failedError: normalize(candidate.failedError),
      callbackMessage: normalize(newsCandidate.callbackMessage),
      summary: normalize(newsCandidate.summary),
    };
  }

  if (kind === "knowledge") {
    const knowledgeCandidate = candidate as Partial<KnowledgeJobNotification>;
    const userId = normalize(knowledgeCandidate.userId);
    const knowledgeItemId = normalize(knowledgeCandidate.knowledgeItemId);
    const status = knowledgeCandidate.status;
    const type =
      knowledgeCandidate.type === "pdf" ||
      knowledgeCandidate.type === "url" ||
      knowledgeCandidate.type === "executive_memo" ||
      knowledgeCandidate.type === "company_research"
        ? knowledgeCandidate.type
        : undefined;
    if (!userId || !knowledgeItemId || !isResolvedOrProcessingStatus(status)) {
      return null;
    }
    const normalizedTitle = normalize(knowledgeCandidate.title);
    const title = type
      ? getKnowledgeItemDisplayName({ type, title: normalizedTitle ?? "", original_filename: null })
      : normalizedTitle;
    const normalizedFailure = normalize(knowledgeCandidate.failedError);
    const failedError = normalizedFailure && !/(?:https?:|www\.|\/\/|%2f|\\|[?#][^\s=]*=|(?:token|secret|key)=)/iu.test(normalizedFailure)
      ? normalizedFailure
      : undefined;
    return {
      kind,
      userId,
      knowledgeItemId,
      status,
      processedAt: normalize(knowledgeCandidate.processedAt),
      title,
      type,
      failedError,
    };
  }

  if (kind === "level_up") {
    const characterId = normalize((candidate as Partial<LevelUpNotification>).characterId);
    const rankName = normalize((candidate as Partial<LevelUpNotification>).rankName);
    if (!characterId || !rankName) {
      return null;
    }
    return {
      kind,
      characterId,
      rankName,
    };
  }

  return null;
}

function dispatchJobNotificationEvent(notifications: PendingJobNotification[]) {
  if (!hasWindow()) return;

  window.dispatchEvent(
    new CustomEvent<PendingJobNotification[]>(JOB_NOTIFICATION_EVENT, {
      detail: notifications,
    })
  );
}

function writePendingJobNotifications(notifications: PendingJobNotification[]) {
  if (!hasWindow()) return;

  if (!notifications.length) {
    window.localStorage.removeItem(JOB_NOTIFICATION_STORAGE_KEY);
    dispatchJobNotificationEvent([]);
    return;
  }

  window.localStorage.setItem(JOB_NOTIFICATION_STORAGE_KEY, JSON.stringify(notifications));
  dispatchJobNotificationEvent(notifications);
}

export function getPendingJobNotificationKey(notification: PendingJobNotification) {
  if (notification.kind === "level_up") {
    return [notification.kind, notification.characterId, notification.rankName].join(":");
  }

  if (notification.kind === "receipt") {
    return [
      notification.kind,
      notification.jobId,
      notification.status,
      notification.processedAt ?? "",
      notification.failedStage ?? "",
      notification.failedError ?? "",
      notification.extractedSummary ?? "",
      "",
    ].join(":");
  }

  if (notification.kind === "receipt_needs_review") {
    return [notification.kind, notification.jobId].join(":");
  }

  if (notification.kind === "executive_memo") {
    return [
      notification.kind,
      notification.jobId,
      notification.status,
      notification.processedAt ?? "",
      notification.memoId ?? "",
      notification.failedStage ?? "",
      notification.failedError ?? "",
      notification.title ?? "",
      notification.summary ?? "",
    ].join(":");
  }

  if (notification.kind === "knowledge") {
    return [notification.kind, notification.userId, notification.knowledgeItemId].join(":");
  }

  return [
    notification.kind,
    notification.jobId,
    notification.status,
    notification.processedAt ?? "",
    notification.failedStage ?? "",
    notification.failedError ?? "",
    notification.keyword ?? "",
    notification.notificationType ?? "",
    notification.reportRunId ?? "",
  ].join(":");
}

export function readPendingJobNotifications(kind?: PendingJobNotificationKind) {
  if (!hasWindow()) return [] as PendingJobNotification[];

  const raw = window.localStorage.getItem(JOB_NOTIFICATION_STORAGE_KEY);
  if (!raw) return [] as PendingJobNotification[];

  try {
    const parsed = JSON.parse(raw);
    const values = Array.isArray(parsed) ? parsed : [parsed];
    const seen = new Set<string>();

    return values
      .map((value) => parsePendingJobNotification(value))
      .filter((value): value is PendingJobNotification => value !== null)
      .filter((notification) => (kind ? notification.kind === kind : true))
      .filter((notification) => {
        const key = getPendingJobNotificationKey(notification);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
  } catch {
    return [] as PendingJobNotification[];
  }
}

export function readPendingJobNotification(kind?: PendingJobNotificationKind) {
  return readPendingJobNotifications(kind)[0] ?? null;
}

export function writePendingJobNotification(notification: PendingJobNotification) {
  const normalized = parsePendingJobNotification(notification);
  if (!normalized) return;

  const nextKey = getPendingJobNotificationKey(normalized);
  const nextNotifications = [
    normalized,
    ...readPendingJobNotifications().filter(
      (item) => getPendingJobNotificationKey(item) !== nextKey
    ),
  ];

  writePendingJobNotifications(nextNotifications);
}

export function removePendingJobNotification(notification: PendingJobNotification) {
  const nextKey = getPendingJobNotificationKey(notification);
  const nextNotifications = readPendingJobNotifications().filter(
    (item) => getPendingJobNotificationKey(item) !== nextKey
  );

  writePendingJobNotifications(nextNotifications);
}

export function clearPendingJobNotifications(kind?: PendingJobNotificationKind) {
  if (!kind) {
    writePendingJobNotifications([]);
    return;
  }

  const nextNotifications = readPendingJobNotifications().filter(
    (item) => item.kind !== kind
  );
  writePendingJobNotifications(nextNotifications);
}

export function consumePendingJobNotification(kind?: PendingJobNotificationKind) {
  const notifications = readPendingJobNotifications();
  const index = notifications.findIndex((notification) => (kind ? notification.kind === kind : true));

  if (index < 0) {
    return null;
  }

  const [notification] = notifications.splice(index, 1);
  writePendingJobNotifications(notifications);
  return notification;
}
